import type { IMenuItem } from '../types/user';

/** 菜单可见性判断方式。 */
export type NavigationPermissionMode = 'any' | 'all';

/**
 * 导航与角色权限树的统一配置。
 *
 * `permissions` 决定菜单是否显示；`rolePermissionCodes` 决定角色权限树中
 * 哪些权限展示在该菜单节点下。两者分开是因为一个菜单可能由多个权限任选其一
 * 即可进入，而页面内仍包含多组可独立分配的操作权限。
 */
export interface NavigationDefinition extends IMenuItem {
  permissions?: string[];
  permissionMode?: NavigationPermissionMode;
  /** 仅超级管理员可见的全局管理入口。 */
  superOnly?: boolean;
  rolePermissionCodes?: string[];
  permissionHint?: string;
  children?: NavigationDefinition[];
}

/**
 * 产品导航结构的唯一配置入口。
 *
 * 侧边栏与角色权限树都从此配置派生，菜单调整时必须同步声明对应权限归属。
 */
export const NAVIGATION_DEFINITIONS: NavigationDefinition[] = [
  {
    key: 'stats',
    title: '数据看板',
    icon: 'ri-dashboard-3-line',
    path: '/dashboard',
    permissions: ['stats:view'],
    rolePermissionCodes: ['stats:view'],
  },
  {
    key: 'crm',
    title: '客资',
    icon: 'ri-user-search-line',
    children: [
      {
        key: 'crm-record',
        title: '客资',
        icon: 'ri-contacts-book-3-line',
        path: '/crm',
        permissions: ['lead:view'],
        rolePermissionCodes: [
          'lead:view',
          'lead:receive-notification',
          'lead:create',
          'lead:edit',
          'lead:assign',
          'lead:import',
          'lead:export',
          'lead:transfer',
        ],
      },
      {
        key: 'crm-recycle-bin',
        title: '客资回收站',
        icon: 'ri-recycle-line',
        path: '/crm/recycle-bin',
        permissions: ['lead:delete'],
        rolePermissionCodes: ['lead:delete'],
        permissionHint: '该入口与删除、恢复客资共用“删除客资”权限。',
      },
    ],
  },
  {
    key: 'org-structure',
    title: '组织架构',
    icon: 'ri-organization-chart',
    children: [
      {
        key: 'dealer',
        title: '组织管理',
        icon: 'ri-building-2-line',
        path: '/org',
        permissions: ['dealer:view'],
        rolePermissionCodes: [
          'dealer:view',
          'dealer:import',
          'dealer:edit',
          'department:view',
          'department:edit',
          'department:sync',
          'department:import',
          'wecom:view',
          'wecom:edit',
        ],
      },
      {
        key: 'member-mgmt',
        title: '成员管理',
        icon: 'ri-team-line',
        path: '/org/members',
        permissions: ['user:view'],
        rolePermissionCodes: [
          'user:view',
          'user:create',
          'user:edit',
          'user:delete',
          'user:reset-password',
          'user:manage-membership',
          'user:assign-role',
          'user:manage-wechat-binding',
        ],
      },
    ],
  },
  {
    key: 'system',
    title: '系统管理',
    icon: 'ri-settings-3-line',
    children: [
      {
        key: 'role',
        title: '角色管理',
        icon: 'ri-shield-user-line',
        path: '/roles',
        permissions: ['role:view'],
        superOnly: true,
        rolePermissionCodes: ['role:view', 'role:create', 'role:edit', 'role:delete'],
      },
      {
        key: 'settings',
        title: '系统设置',
        icon: 'ri-settings-4-line',
        path: '/settings',
        permissions: ['platform:view', 'custom-field:view', 'lead:settings', 'wechat-official:view'],
        permissionMode: 'any',
        rolePermissionCodes: [
          'platform:view',
          'platform:manage',
          'custom-field:view',
          'custom-field:manage',
          'lead:settings',
          'wechat-official:view',
          'wechat-official:edit',
        ],
      },
    ],
  },
  {
    key: 'platform-logs',
    title: '数据接入',
    icon: 'ri-database-2-line',
    children: [
      {
        key: 'webhook-push-logs',
        title: '推送日志',
        icon: 'ri-send-plane-2-line',
        path: '/platform-logs/webhook',
        permissions: ['platform:view'],
        permissionHint: '该入口由“查看平台接入设置”权限同步控制。',
      },
      {
        key: 'raw-lead-list',
        title: '原始客资',
        icon: 'ri-inbox-archive-line',
        path: '/platform-logs/raw-leads',
        permissions: ['raw-lead:view'],
        rolePermissionCodes: ['raw-lead:view', 'raw-lead:view-all'],
      },
    ],
  },
];

/** 根据菜单 key 获取与菜单配置一致的路由权限要求。 */
export function getNavigationPermission(key: string): string | string[] | undefined {
  const pending = [...NAVIGATION_DEFINITIONS];
  while (pending.length > 0) {
    const item = pending.shift()!;
    if (item.key === key) {
      if (!item.permissions?.length) return undefined;
      return item.permissions.length === 1 ? item.permissions[0] : [...item.permissions];
    }
    if (item.children?.length) pending.push(...item.children);
  }
  return undefined;
}
